import { useEffect, useState } from "react"

export interface TripClock {
  today: string
  now: string
}

const TICK_INTERVAL_MS = 60_000

// Reads "what time it is on the trip's clock" from a single instant, in the
// trip's own IANA time zone (FR-1.1) — the one place every public page that
// needs "today" or "now" reads it from, instead of deriving it per page. It
// holds no domain meaning (what counts as "past" is each page's own
// predicate, FR-1.5, kept out of this module by plan ruling) and formats no
// display copy — only the two machine strings FR-1.2 names.
//
// `at` is a required parameter, never a defaulted `new Date()` (FR-1.4) —
// the strongest test injection, and it is what keeps every `new Date()` call
// inside `useTripClock`'s `useState` initializer and its interval/visibility
// callbacks below, none of them reachable from render, so React's `purity`
// lint rule has nothing to flag (NFR-3).
function readClock(timeZone: string, at: Date): TripClock {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    // Named explicitly, not left to `hour12: false` to imply (R1): "h23"
    // reads midnight as "00", never the "24" hourCycle "h24" would produce
    // — a "24:00" reading would silently break every strict `<` comparison
    // the two page domains do downstream (`time24 < now`), since nothing
    // would ever compare as past right after midnight.
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(at)

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? ""
  // formatToParts is asked for 2-digit fields throughout, but a locale is
  // still free to hand back a single character (e.g. some hour renderings) —
  // padStart is the belt this module wears regardless (FR-1.2).
  const pad = (value: string) => value.padStart(2, "0")

  return {
    today: `${part("year")}-${pad(part("month"))}-${pad(part("day"))}`,
    now: `${pad(part("hour"))}:${pad(part("minute"))}`,
  }
}

// The trip's current date, as ISO text ("2026-09-16") — FR-1.2.
export function todayInTrip(timeZone: string, at: Date): string {
  return readClock(timeZone, at).today
}

// The trip's current time, as a fixed 24-hour zero-padded string ("01:30")
// — FR-1.2.
export function nowInTrip(timeZone: string, at: Date): string {
  return readClock(timeZone, at).now
}

// Keeps a component's `{ today, now }` fresh: re-read once a minute and
// whenever the tab becomes visible again (FR-1.3), so an open page corrects
// itself at midnight and a restored tab is right without a reload (G3).
// Both re-reads happen inside the effect's own callbacks, and the initial
// read inside `useState`'s lazy initializer — never in the component body
// itself — so there is no clock read during render for `purity` to flag,
// and no synchronous `setState` call during the effect's own execution (the
// writes happen later, from the interval/visibility callbacks) for
// `set-state-in-effect` to flag either (NFR-3). The interval and the
// listener are both released on unmount (NFR-4).
export function useTripClock(timeZone: string): TripClock {
  const [clock, setClock] = useState(() => readClock(timeZone, new Date()))

  useEffect(() => {
    const tick = () => setClock(readClock(timeZone, new Date()))

    const intervalId = setInterval(tick, TICK_INTERVAL_MS)
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") tick()
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [timeZone])

  return clock
}
